import { TCompany, TPortfolioCompany } from "../../const/const";
import { CompanyCell } from "./CompanyCell/CompanyCell";
import { InvestorsCell } from "./InvestorsCell/InvestorsCell";
import { FinancialStageCell } from "./FinancialStageCell/FinancialStageCell";
import { TotalFundingCell } from "./TotalFundingCell/TotalFundingCell";
import { LocationCell } from "./LocationCell/LocationCell";
import { WebsiteCell } from "./WebsiteCell/WebsiteCell";
import { headers } from "../TableLabels/const/const";
import styles from "./RowCell.module.scss";
import { ActionCell } from "./ActionCell/ActionCell";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { MoveIcon, ProductIcon, TagIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { Typography } from "@/frontend/shared/ui/Typography";
import { Dispatch, SetStateAction, useState } from "react";
import { unarchiveCompany } from "@/frontend/pages/DashboardPage/model/services/unarchiveCompany";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { useQueryClient } from "@tanstack/react-query";
import { FoundingDateCell } from "./FoundingDateCell/FoundingDateCell";

type TRowCellProps = {
  colId: string;
  data: TPortfolioCompany;
  selected: number[];
  setShownModal: Dispatch<
    SetStateAction<{
      type: string;
      id: number;
    }>
  >;
};

type TCompanyIndexable = TCompany & { [key: string]: any };

export function RowCell({ colId, data, setShownModal }: TRowCellProps) {
  const field = (data.companyData as TCompanyIndexable)[colId];
  const [shownDropshadow, setShownDropshadow] = useState(false);
  const queryClient = useQueryClient();

  const onUnarchive = () => {
    setShownDropshadow(false);
    unarchiveCompany([data.id])
      .then(() => {
        snackbarConstructor("Company has been unarchived", "normal");
        queryClient.invalidateQueries({ queryKey: ["allArchivedCompaniesData"] });
      })
      .catch(() => {
        snackbarConstructor("Error unarchiving company", "error");
      });
  };

  switch (colId) {
    case "name":
      return <CompanyCell field={data} />;
    case "financialStage":
      return <FinancialStageCell field={field} />;
    case "foundingDate":
      return <FoundingDateCell field={field} />;
    case "totalFunding":
      return <TotalFundingCell field={field} />;
    case "investors":
      return <InvestorsCell field={data.companyData.harmonicInvestors || []} />;
    case "city":
      return <LocationCell field={data.companyData} />;
    case "website":
      return <WebsiteCell field={field} />;
    case "actions":
      return (
        <div className={styles.actionsContainer} onClick={(e) => e.stopPropagation()}>
          <DropshadowMenu
            isOpen={shownDropshadow}
            onClose={() => setShownDropshadow(false)}
            options={[
              <div className={styles.dropshadowOption} onClick={() => setShownModal({ type: "addTags", id: data.id })}>
                <TagIcon />
                <Typography variant="label-4" color="greyscale800">
                  Modify Tags
                </Typography>
              </div>,
              // <div className={styles.dropshadowOption} onClick={() => setShownModal({ type: "move", id: data.id })}>
              //   <MoveIcon />
              //   <Typography variant="label-4" color="greyscale800">
              //     Move
              //   </Typography>
              // </div>,
              <div className={styles.dropshadowOption} onClick={onUnarchive}>
                <ProductIcon />
                <Typography variant="label-4" color="greyscale800">
                  Unarchive
                </Typography>
              </div>,
              <div className={styles.dropshadowOption} onClick={() => setShownModal({ type: "delete", id: data.id })}>
                <TrashIcon />
                <Typography variant="label-4" color="greyscale800">
                  Delete
                </Typography>
              </div>
            ]}
            top="0"
            right="0"
            dividerIndexRate={[1, 3]}
          />
          <ActionCell
            field={field}
            onChange={() => {
              setShownDropshadow(true);
            }}
          />
        </div>
      );
    default:
      return null;
  }
}

export function buildCells(
  portfolioCompany: TPortfolioCompany,
  selected: number[],
  setShownModal: Dispatch<
    SetStateAction<{
      type: string;
      id: number;
    }>
  >
) {
  return headers.map((header) => (
    <div key={header.key} className={styles.cell}>
      <RowCell data={portfolioCompany} colId={header.key} selected={selected} setShownModal={setShownModal} />
    </div>
  ));
}
